import { FirebaseApp, getApp, getApps, initializeApp } from 'firebase/app';
import {
	Auth,
	ConfirmationResult,
	RecaptchaVerifier,
	getAuth,
	initializeRecaptchaConfig,
	signInWithPhoneNumber,
} from 'firebase/auth';
import { fetchAndActivate, getRemoteConfig, getValue, RemoteConfig } from 'firebase/remote-config';

import { FirebaseConfig } from './firebase-config';

import { postOtpAuthenticationRequest, postOtpLookupRequest } from '@src/auth/otp-client-shared';
import { getAnalyticsInstance } from '@src/lib/analytics/firebase-analytics';
import { getLogger } from '@src/lib/logging/logger';

const logger = getLogger('app');

let firebaseConfig: FirebaseConfig | null = null;

export const PHONE_OTP_RECAPTCHA_CONTAINER_ID = 'phone-otp-recaptcha';

type FirebasePhoneOtpSendCodeUiErrorCode =
	| 'INVALID_RECAPTCHA_CREDENTIAL'
	| 'INVALID_FIREBASE_CREDENTIAL'
	| 'TOO_MANY_ATTEMPTS_TRY_LATER'
	| 'UNAUTHORIZED_DOMAIN'
	| 'PHONE_AUTH_DISABLED'
	| 'INVALID_API_KEY'
	| 'BILLING_NOT_ENABLED';

export type PhoneOtpSendCodeUiErrorCode = 'PHONE_VERIFICATION_UNAVAILABLE' | 'TOO_MANY_ATTEMPTS_TRY_LATER';

type SendCodeErrorContext = {
	errorCode: string | null;
	errorMessage: string;
	backendCode: string | null;
	isInvalidRecaptchaCredential: boolean;
};

type SendCodeErrorRule = {
	matches: (context: SendCodeErrorContext) => boolean;
	uiErrorCode: FirebasePhoneOtpSendCodeUiErrorCode;
};

type InitializeRecaptchaOptions = {
	onError?: () => void;
};

type SendPhoneOtpVerificationCodeOptions = {
	normalizedPhoneNumber: string;
	onExpired: () => void;
	recaptchaContainerId?: string;
	onRecaptchaConfigInitError?: () => void;
};

type SendPhoneOtpVerificationCodeResult =
	| {
			ok: true;
			confirmationResult: ConfirmationResult;
	  }
	| {
			ok: false;
			reason: 'AUTH_UNAVAILABLE';
	  };

export type FirebaseSendCodeErrorDetails = {
	errorCode: string | null;
	errorMessage: string;
	firebaseUiErrorCode: FirebasePhoneOtpSendCodeUiErrorCode | null;
	uiErrorCode: PhoneOtpSendCodeUiErrorCode;
};

export type PhoneOtpLoginUiErrorCode =
	| 'ACCOUNT_NOT_FOUND'
	| 'ACCOUNT_INACTIVE'
	| 'PHONE_VERIFICATION_UNAVAILABLE'
	| 'TOO_MANY_ATTEMPTS_TRY_LATER'
	| 'INVALID_PHONE_NUMBER'
	| 'VERIFICATION_EXPIRED'
	| 'REQUEST_CODE_FIRST'
	| 'OTP_CODE_REQUIRED'
	| 'REQUEST_NEW_CODE'
	| 'LOGIN_UNAVAILABLE'
	| 'INVALID_CODE';

const LOGIN_UNAVAILABLE_MESSAGE = 'Login could not be completed at this time.';

export const PHONE_OTP_UI_ERROR_MESSAGES: Record<PhoneOtpLoginUiErrorCode, string> = {
	ACCOUNT_NOT_FOUND: LOGIN_UNAVAILABLE_MESSAGE,
	ACCOUNT_INACTIVE: LOGIN_UNAVAILABLE_MESSAGE,
	PHONE_VERIFICATION_UNAVAILABLE:
		'Phone verification is temporarily unavailable. Please try again later, or contact support.',
	TOO_MANY_ATTEMPTS_TRY_LATER: 'Too many attempts. Please wait an hour and try again.',
	INVALID_PHONE_NUMBER: 'Enter a valid phone number including area code.',
	VERIFICATION_EXPIRED: 'Verification expired. Please request a new code.',
	REQUEST_CODE_FIRST: 'Request a one-time code first.',
	OTP_CODE_REQUIRED: 'Enter the 6-digit code sent to your phone.',
	REQUEST_NEW_CODE: 'Please request a new code and try again.',
	LOGIN_UNAVAILABLE: LOGIN_UNAVAILABLE_MESSAGE,
	INVALID_CODE: 'Invalid code. Please try again.',
};

export const getPhoneOtpApiEndpoints = (appPath: string) => ({
	clientPhoneLookupEndpoint: `${appPath}/api/client-phone-lookup`,
	clientPhoneAuthenticateEndpoint: `${appPath}/api/client-phone-authenticate`,
});

type LookupClientResult =
	| { ok: true; lookupToken: string }
	| { ok: false; uiErrorCode: 'ACCOUNT_NOT_FOUND' | 'ACCOUNT_INACTIVE' };

export type SendPhoneOtpLoginCodeResult =
	| {
			ok: true;
			confirmationResult: ConfirmationResult;
			lookupToken: string;
	  }
	| {
			ok: false;
			uiErrorCode: PhoneOtpLoginUiErrorCode;
			cause: 'LOOKUP' | 'INVALID_PHONE' | 'AUTH_UNAVAILABLE' | 'FIREBASE_SEND_CODE';
			firebaseErrorDetails?: FirebaseSendCodeErrorDetails;
	  };

export type VerifyPhoneOtpLoginCodeResult =
	| { ok: true; sessionToken: string }
	| {
			ok: false;
			uiErrorCode: PhoneOtpLoginUiErrorCode;
			error?: unknown;
	  };

declare global {
	var recaptchaVerifier: RecaptchaVerifier | undefined;
	var recaptchaWidgetId: number | undefined;
}

const SEND_CODE_UI_ERROR_CODES: Record<FirebasePhoneOtpSendCodeUiErrorCode, PhoneOtpSendCodeUiErrorCode> = {
	INVALID_RECAPTCHA_CREDENTIAL: 'PHONE_VERIFICATION_UNAVAILABLE',
	INVALID_FIREBASE_CREDENTIAL: 'PHONE_VERIFICATION_UNAVAILABLE',
	TOO_MANY_ATTEMPTS_TRY_LATER: 'TOO_MANY_ATTEMPTS_TRY_LATER',
	UNAUTHORIZED_DOMAIN: 'PHONE_VERIFICATION_UNAVAILABLE',
	PHONE_AUTH_DISABLED: 'PHONE_VERIFICATION_UNAVAILABLE',
	INVALID_API_KEY: 'PHONE_VERIFICATION_UNAVAILABLE',
	BILLING_NOT_ENABLED: 'PHONE_VERIFICATION_UNAVAILABLE',
};

const SEND_CODE_ERROR_RULES: SendCodeErrorRule[] = [
	{
		matches: ({ errorCode, isInvalidRecaptchaCredential }) =>
			errorCode === 'auth/invalid-app-credential' && isInvalidRecaptchaCredential,
		uiErrorCode: 'INVALID_RECAPTCHA_CREDENTIAL',
	},
	{
		matches: ({ errorCode }) => errorCode === 'auth/invalid-app-credential',
		uiErrorCode: 'INVALID_FIREBASE_CREDENTIAL',
	},
	{
		matches: ({ errorCode }) => errorCode === 'auth/captcha-check-failed',
		uiErrorCode: 'INVALID_RECAPTCHA_CREDENTIAL',
	},
	{
		matches: ({ errorCode, errorMessage }) =>
			errorCode === 'auth/too-many-requests' || errorMessage.includes('TOO_MANY_ATTEMPTS_TRY_LATER'),
		uiErrorCode: 'TOO_MANY_ATTEMPTS_TRY_LATER',
	},
	{
		matches: ({ errorCode }) => errorCode === 'auth/unauthorized-domain',
		uiErrorCode: 'UNAUTHORIZED_DOMAIN',
	},
	{
		matches: ({ errorCode, backendCode }) =>
			errorCode === 'auth/operation-not-allowed' || backendCode === 'OPERATION_NOT_ALLOWED',
		uiErrorCode: 'PHONE_AUTH_DISABLED',
	},
	{
		matches: ({ errorCode, backendCode }) =>
			errorCode === 'auth/invalid-api-key' || backendCode === 'INVALID_API_KEY',
		uiErrorCode: 'INVALID_API_KEY',
	},
	{
		matches: ({ backendCode }) => backendCode === 'BILLING_NOT_ENABLED',
		uiErrorCode: 'BILLING_NOT_ENABLED',
	},
];

const getErrorStringField = (error: unknown, field: 'code' | 'message'): string | null =>
	typeof error === 'object' && error && field in error && typeof error[field] === 'string' ? error[field] : null;

const getFirebaseBackendErrorCode = (errorMessage: string): string | null => {
	const firebaseBackendCodeMatch = /\[\s*([A-Z_]+)\s*\]/.exec(errorMessage);
	return firebaseBackendCodeMatch?.[1] || null;
};

const resetRecaptchaWidget = () => {
	if (typeof globalThis.recaptchaWidgetId !== 'number') {
		return;
	}

	const grecaptcha = (globalThis as { grecaptcha?: { enterprise?: { reset?: (widgetId: number) => void } } })
		.grecaptcha;
	grecaptcha?.enterprise?.reset?.(globalThis.recaptchaWidgetId);
};

const tryInitializeRecaptchaConfig = async (auth: Auth, options: InitializeRecaptchaOptions = {}) => {
	try {
		await initializeRecaptchaConfig(auth);
	} catch {
		// Best effort only: fallback to RecaptchaVerifier flow if Firebase config prefetch fails.
		options.onError?.();
	}
};

const runSendCodeAttempt = async ({
	auth,
	normalizedPhoneNumber,
	onExpired,
	recaptchaContainerId,
}: {
	auth: Auth;
	normalizedPhoneNumber: string;
	onExpired: () => void;
	recaptchaContainerId: string;
}): Promise<ConfirmationResult> => {
	globalThis.recaptchaVerifier ??= new RecaptchaVerifier(auth, recaptchaContainerId, {
		size: 'invisible',
		'expired-callback': () => {
			clearRecaptchaVerifier(recaptchaContainerId);
			onExpired();
		},
	});

	if (typeof globalThis.recaptchaWidgetId !== 'number') {
		globalThis.recaptchaWidgetId = await globalThis.recaptchaVerifier.render();
	}

	resetRecaptchaWidget();

	return signInWithPhoneNumber(auth, normalizedPhoneNumber, globalThis.recaptchaVerifier);
};

export const setFirebaseConfig = (config: FirebaseConfig): void => {
	firebaseConfig = config;
};

export const getFirebaseApp = (): FirebaseApp => {
	if (!firebaseConfig) {
		throw new Error('Firebase config not initialized. Call setFirebaseConfig first.');
	}

	if (getApps().length) {
		try {
			return getApp();
		} catch (error: unknown) {
			const message = error instanceof Error ? error.message : 'Unknown getApp error';
			logger.error({ error, message }, 'FIREBASE_GET_APP_FAILED');
		}
	}

	try {
		return initializeApp(firebaseConfig);
	} catch (error: unknown) {
		const message = error instanceof Error ? error.message : 'Unknown initializeApp error';
		logger.error({ error, message }, 'FIREBASE_INITIALIZE_APP_FAILED');
		throw error instanceof Error ? error : new Error(message);
	}
};

export const getFirebaseAuth = () => {
	const app = getFirebaseApp();
	return getAuth(app);
};

export const normalizePhoneNumber = (phoneNumber: string): string | null => {
	const hasExplicitCountryCode = phoneNumber.trim().startsWith('+');
	const digitsOnly = phoneNumber.replaceAll(/[^\d]/g, '');

	if (!digitsOnly) {
		return null;
	}

	if (hasExplicitCountryCode) {
		return `+${digitsOnly}`;
	}

	if (digitsOnly.length === 10) {
		return `+1${digitsOnly}`;
	}

	if (digitsOnly.length === 11 && digitsOnly.startsWith('1')) {
		return `+${digitsOnly}`;
	}

	return null;
};

export const clearRecaptchaVerifier = (recaptchaContainerId = PHONE_OTP_RECAPTCHA_CONTAINER_ID) => {
	if (globalThis.recaptchaVerifier) {
		globalThis.recaptchaVerifier.clear();
		globalThis.recaptchaVerifier = undefined;
	}
	globalThis.recaptchaWidgetId = undefined;

	const recaptchaContainer = globalThis.document?.getElementById(recaptchaContainerId);
	if (recaptchaContainer) {
		recaptchaContainer.replaceChildren();
	}
};

export const sendPhoneOtpVerificationCode = async ({
	normalizedPhoneNumber,
	onExpired,
	recaptchaContainerId = PHONE_OTP_RECAPTCHA_CONTAINER_ID,
	onRecaptchaConfigInitError,
}: SendPhoneOtpVerificationCodeOptions): Promise<SendPhoneOtpVerificationCodeResult> => {
	const auth = getFirebaseAuth();
	if (!auth) {
		return { ok: false, reason: 'AUTH_UNAVAILABLE' };
	}

	await tryInitializeRecaptchaConfig(auth, {
		onError: onRecaptchaConfigInitError,
	});

	const confirmationResult = await runSendCodeAttempt({
		auth,
		normalizedPhoneNumber,
		onExpired,
		recaptchaContainerId,
	});

	return {
		ok: true,
		confirmationResult,
	};
};

export const confirmPhoneOtpVerificationCode = async ({
	confirmationResult,
	verificationCode,
}: {
	confirmationResult: ConfirmationResult;
	verificationCode: string;
}): Promise<string> => {
	const userCredential = await confirmationResult.confirm(verificationCode.trim());
	return userCredential.user.getIdToken();
};

export const getFirebaseSendCodeErrorDetails = (
	error: unknown,
	{ shouldLogFirebaseErrorCode = false }: { shouldLogFirebaseErrorCode?: boolean } = {}
): FirebaseSendCodeErrorDetails => {
	const errorCode = getErrorStringField(error, 'code');
	const errorMessage = getErrorStringField(error, 'message') || '';
	const backendCode = getFirebaseBackendErrorCode(errorMessage);
	const isInvalidRecaptchaCredential =
		errorCode === 'auth/captcha-check-failed' ||
		errorMessage.includes('NO_RECAPTCHA') ||
		errorMessage.includes('RECAPTCHA_ENTERPRISE');

	const context: SendCodeErrorContext = {
		errorCode,
		errorMessage,
		backendCode,
		isInvalidRecaptchaCredential,
	};

	const matchedRule = SEND_CODE_ERROR_RULES.find((rule) => rule.matches(context));
	const firebaseUiErrorCode = matchedRule?.uiErrorCode || null;

	if (firebaseUiErrorCode && shouldLogFirebaseErrorCode) {
		console.error(`Firebase error code: ${firebaseUiErrorCode}`);
	}

	return {
		errorCode,
		errorMessage,
		firebaseUiErrorCode,
		uiErrorCode: firebaseUiErrorCode
			? SEND_CODE_UI_ERROR_CODES[firebaseUiErrorCode]
			: 'PHONE_VERIFICATION_UNAVAILABLE',
	};
};

const lookupMatchingClient = async ({
	clientPhoneLookupEndpoint,
	familyName,
	phoneNumber,
	redirectTo,
	facility,
	timeSlot,
	mainReasonForVisit,
	specificReasons,
	isProd,
}: {
	clientPhoneLookupEndpoint: string;
	familyName: string;
	phoneNumber: string;
	redirectTo?: string;
	facility?: string;
	timeSlot?: {
		date?: string | null;
		providerId?: string | null;
		time?: string | null;
	} | null;
	mainReasonForVisit?: unknown;
	specificReasons?: string;
	isProd: boolean;
}): Promise<LookupClientResult> => {
	try {
		const { response, result } = await postOtpLookupRequest({
			endpoint: clientPhoneLookupEndpoint,
			payload: {
				familyName,
				phoneNumber,
				redirectTo,
				facility,
				timeSlot,
				mainReasonForVisit,
				specificReasons,
			},
		});
		if (!response.ok || !result.matched || !result.lookupToken) {
			if (result.reason === 'inactive') {
				return { ok: false, uiErrorCode: 'ACCOUNT_INACTIVE' };
			}
			return { ok: false, uiErrorCode: 'ACCOUNT_NOT_FOUND' };
		}

		return { ok: true, lookupToken: result.lookupToken };
	} catch (error: unknown) {
		if (isProd) {
			console.error('Client phone lookup failed', error);
		}
		return { ok: false, uiErrorCode: 'ACCOUNT_NOT_FOUND' };
	}
};

export const sendPhoneOtpLoginCode = async ({
	phoneNumber,
	familyName,
	clientPhoneLookupEndpoint,
	redirectTo,
	facility,
	timeSlot,
	mainReasonForVisit,
	specificReasons,
	onExpired,
	onRecaptchaConfigInitError,
	shouldLogFirebaseErrorCode,
	isProd,
}: {
	phoneNumber: string;
	familyName: string;
	clientPhoneLookupEndpoint: string;
	redirectTo?: string;
	facility?: string;
	timeSlot?: {
		date?: string | null;
		providerId?: string | null;
		time?: string | null;
	} | null;
	mainReasonForVisit?: unknown;
	specificReasons?: string;
	onExpired: () => void;
	onRecaptchaConfigInitError?: () => void;
	shouldLogFirebaseErrorCode: boolean;
	isProd: boolean;
}): Promise<SendPhoneOtpLoginCodeResult> => {
	const normalizedPhoneNumber = normalizePhoneNumber(phoneNumber);
	if (!normalizedPhoneNumber) {
		return {
			ok: false,
			uiErrorCode: 'INVALID_PHONE_NUMBER',
			cause: 'INVALID_PHONE',
		};
	}

	const lookupResult = await lookupMatchingClient({
		clientPhoneLookupEndpoint,
		familyName,
		phoneNumber: phoneNumber.trim(),
		redirectTo,
		facility,
		timeSlot,
		mainReasonForVisit,
		specificReasons,
		isProd,
	});

	if (!lookupResult.ok) {
		return {
			ok: false,
			uiErrorCode: lookupResult.uiErrorCode,
			cause: 'LOOKUP',
		};
	}

	try {
		const sendCodeResult = await sendPhoneOtpVerificationCode({
			normalizedPhoneNumber,
			recaptchaContainerId: PHONE_OTP_RECAPTCHA_CONTAINER_ID,
			onExpired,
			onRecaptchaConfigInitError,
		});

		if (!sendCodeResult.ok) {
			return {
				ok: false,
				uiErrorCode: 'PHONE_VERIFICATION_UNAVAILABLE',
				cause: 'AUTH_UNAVAILABLE',
			};
		}

		return {
			ok: true,
			confirmationResult: sendCodeResult.confirmationResult,
			lookupToken: lookupResult.lookupToken,
		};
	} catch (error: unknown) {
		clearRecaptchaVerifier();
		const firebaseErrorDetails = getFirebaseSendCodeErrorDetails(error, {
			shouldLogFirebaseErrorCode,
		});

		return {
			ok: false,
			uiErrorCode: firebaseErrorDetails.uiErrorCode,
			cause: 'FIREBASE_SEND_CODE',
			firebaseErrorDetails,
		};
	}
};

export const verifyPhoneOtpLoginCode = async ({
	confirmationResult,
	verificationCode,
	lookupToken,
	clientPhoneAuthenticateEndpoint,
}: {
	confirmationResult: ConfirmationResult | null;
	verificationCode: string;
	lookupToken: string;
	clientPhoneAuthenticateEndpoint: string;
}): Promise<VerifyPhoneOtpLoginCodeResult> => {
	if (!confirmationResult) {
		return { ok: false, uiErrorCode: 'REQUEST_CODE_FIRST' };
	}

	if (!verificationCode || verificationCode.trim().length < 6) {
		return { ok: false, uiErrorCode: 'OTP_CODE_REQUIRED' };
	}

	if (!lookupToken) {
		return { ok: false, uiErrorCode: 'REQUEST_NEW_CODE' };
	}

	try {
		const firebaseIdToken = await confirmPhoneOtpVerificationCode({
			confirmationResult,
			verificationCode,
		});

		const { response: authenticationResponse, result: authenticationResult } = await postOtpAuthenticationRequest({
			endpoint: clientPhoneAuthenticateEndpoint,
			body: {
				lookupToken,
				firebaseIdToken,
			},
		});

		if (!authenticationResponse.ok || !authenticationResult.token) {
			return { ok: false, uiErrorCode: 'LOGIN_UNAVAILABLE' };
		}

		return {
			ok: true,
			sessionToken: authenticationResult.token,
		};
	} catch (error: unknown) {
		const errorCode = getErrorStringField(error, 'code');
		const uiErrorCode = errorCode === 'auth/code-expired' ? 'VERIFICATION_EXPIRED' : 'INVALID_CODE';

		return {
			ok: false,
			uiErrorCode,
			error,
		};
	}
};

/**
 * Firebase Remote Config values consumed by the web app.
 *
 * Keys mirror the parameter names configured in the Firebase Console.
 * Booleans are typed as `boolean` here even though the SDK stores
 * `defaultConfig` as string, because `getValue().asBoolean()` normalizes
 * the read side.
 */
export const REMOTE_CONFIG_DEFAULTS = {
	nc_phone_verification: false,
} as const;

export type RemoteConfigValues = { -readonly [K in keyof typeof REMOTE_CONFIG_DEFAULTS]: boolean };

let remoteConfigInstance: RemoteConfig | null = null;

/**
 * Lazily initialize (and memoize) the Remote Config instance.
 *
 * Returns `null` on the server, in unsupported browsers, or when the Firebase
 * app has not been initialized — callers must handle this gracefully and fall
 * back to `REMOTE_CONFIG_DEFAULTS`.
 */
export const initRemoteConfig = (): RemoteConfig | null => {
	if (globalThis.window === undefined) return null;
	if (remoteConfigInstance) return remoteConfigInstance;

	if (!firebaseConfig) {
		logger.warn({}, 'FIREBASE_REMOTE_CONFIG_CONFIG_NOT_SET');
		return null;
	}

	let app: FirebaseApp;
	try {
		app = getFirebaseApp(); // initializes the Firebase app if not already
	} catch (error: unknown) {
		const message = error instanceof Error ? error.message : 'Unknown getFirebaseApp error';
		logger.error({ error, message }, 'FIREBASE_REMOTE_CONFIG_APP_NOT_INITIALIZED');
		return null;
	}

	try {
		const rc = getRemoteConfig(app);
		rc.settings.minimumFetchIntervalMillis = firebaseConfig.remoteConfigMinFetchInterval ?? 60 * 60 * 1000; // default to 1 hour if not set
		rc.defaultConfig = Object.fromEntries(Object.entries(REMOTE_CONFIG_DEFAULTS).map(([k, v]) => [k, String(v)]));
		remoteConfigInstance = rc;
		return rc;
	} catch (error: unknown) {
		const message = error instanceof Error ? error.message : 'Unknown getRemoteConfig error';
		logger.error({ error, message }, 'FIREBASE_REMOTE_CONFIG_INIT_FAILED');
		return null;
	}
};

/**
 * Initialize Firebase Analytics so Remote Config can enroll the installation in
 * A/B experiments and record exposure on activate. Best-effort: never throws.
 */
const ensureAnalyticsInitialized = async (): Promise<void> => {
	try {
		await getAnalyticsInstance();
	} catch (error: unknown) {
		const message = error instanceof Error ? error.message : 'Unknown getAnalytics error';
		logger.warn({ error, message }, 'FIREBASE_ANALYTICS_INIT_FAILED');
	}
};

/**
 * Fetch and activate Remote Config values.
 *
 * Never throws — analytics/experiment failures must not break the app. On any
 * failure the app-defined `REMOTE_CONFIG_DEFAULTS` are returned so downstream
 * consumers always receive a fully-typed value object.
 */
export const fetchRemoteConfig = async (): Promise<RemoteConfigValues> => {
	const rc = initRemoteConfig();
	if (!rc) return { ...REMOTE_CONFIG_DEFAULTS };

	// Analytics must be live before activate so Firebase A/B Testing enrolls this user and logs exposure.
	await ensureAnalyticsInitialized();

	try {
		await fetchAndActivate(rc);
	} catch (error: unknown) {
		const message = error instanceof Error ? error.message : 'Unknown fetchAndActivate error';
		logger.error({ error, message }, 'FIREBASE_REMOTE_CONFIG_FETCH_FAILED');
		return { ...REMOTE_CONFIG_DEFAULTS };
	}

	try {
		return {
			nc_phone_verification: getValue(rc, 'nc_phone_verification').asBoolean(),
		};
	} catch (error: unknown) {
		const message = error instanceof Error ? error.message : 'Unknown getValue error';
		logger.error({ error, message }, 'FIREBASE_REMOTE_CONFIG_READ_FAILED');
		return { ...REMOTE_CONFIG_DEFAULTS };
	}
};
